import { useEffect, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
// dnd-kit imports
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, Loader2, Plus, X } from 'lucide-react';

import { fetchFunnelTags, updateFunnelTags } from '@/hooks/report';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

const CARD_STYLE =
  'min-w-[140px] h-12 flex items-center justify-center rounded-md border bg-background px-3 py-2';
const TAG_CARD_STYLE =
  'flex items-center gap-2 min-w-[180px] h-12 rounded-md border bg-background px-2 py-2 shadow-sm hover:shadow-md transition-shadow relative';
const DRAGGING_STYLE = 'ring-2 ring-primary shadow-xl scale-105 z-10 ';

function DragHandle(props) {
  // Three vertical dots
  return (
    <div
      {...props}
      className='flex h-full cursor-grab select-none flex-col items-center justify-center pr-2'
      style={{ minWidth: 18 }}
      aria-label='Drag handle'
    >
      <span className='mb-0.5 block h-1 w-1 rounded-full bg-gray-400' />
      <span className='mb-0.5 block h-1 w-1 rounded-full bg-gray-400' />
      <span className='block h-1 w-1 rounded-full bg-gray-400' />
    </div>
  );
}

function SortableTagCard({
  tag,
  onRemove,
  id,
  draggingId,
  isUpdating,
  isNew,
  showArrow,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className='flex items-center'
    >
      <div
        className={
          TAG_CARD_STYLE +
          (isDragging ? ` ${DRAGGING_STYLE}` : '') +
          (draggingId && draggingId !== id ? ' opacity-60' : '')
        }
      >
        {/* Drag handle */}
        <DragHandle {...listeners} />
        <div
          className='mr-1 h-3 w-3 rounded-full'
          style={{ backgroundColor: tag.colour || '#94A3B8' }}
        />
        <span className='whitespace-nowrap text-sm font-medium'>
          {tag.name}
        </span>
        {isNew && (
          <span className='ml-1 text-xs font-medium text-blue-600'>New</span>
        )}
        <Button
          variant='ghost'
          size='icon'
          className='ml-1 h-6 w-6 p-0'
          onClick={() => onRemove(tag._id)}
          tabIndex={-1}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <Loader2 className='h-3 w-3 animate-spin' />
          ) : (
            <X className='h-4 w-4' />
          )}
        </Button>
      </div>
      {showArrow && (
        <ChevronRight className='ml-2 h-6 w-6 text-muted-foreground' />
      )}
    </div>
  );
}

const FunnelTagEditor = ({ onRefresh }) => {
  const { tags } = useInboxStore((state) => ({
    tags: state.tags,
  }));
  const [displayedFunnelTags, setDisplayedFunnelTags] = useState([]);
  const [initialFunnelTagIds, setInitialFunnelTagIds] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch existing funnel tags on component mount
  useEffect(() => {
    const fetchExistingTags = async () => {
      setLoading(true);
      try {
        const response = await fetchFunnelTags();
        // Handle different response structures
        let tagsData = [];
        if (response && response.data) {
          tagsData = response.data;
        } else if (Array.isArray(response)) {
          tagsData = response;
        } else if (response && Array.isArray(response.tags)) {
          tagsData = response.tags;
        } else if (response && Array.isArray(response.funnel_tags)) {
          tagsData = response.funnel_tags;
        }
        setDisplayedFunnelTags(tagsData);
        setInitialFunnelTagIds(new Set(tagsData.map((t) => t._id)));
        setHasUnsavedChanges(false); // Reset on initial load
      } catch (error) {
        console.error('Error fetching funnel tags:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExistingTags();
  }, []);

  // Filter pipeline tags only
  const pipelineTags = tags.filter((tag) => tag.pipeline_tag);

  // Tags that can still be added (pipeline tags that aren't already in funnel)
  const availableTags = pipelineTags.filter(
    (tag) => !displayedFunnelTags.some((t) => t._id === tag._id)
  );

  // Combine existing and selected tags for sorting
  const funnelTagIds = displayedFunnelTags.map((tag) => tag._id);

  // Save changes function
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateFunnelTags(displayedFunnelTags.map((t) => t._id));
      setInitialFunnelTagIds(new Set(displayedFunnelTags.map((t) => t._id)));
      setHasUnsavedChanges(false);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to save funnel tags', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Remove tag handler
  const handleRemove = (tagIdToRemove) => {
    setUpdatingId(tagIdToRemove);
    try {
      setDisplayedFunnelTags((prevTags) =>
        prevTags.filter((t) => t._id !== tagIdToRemove)
      );
      setHasUnsavedChanges(true);
    } catch (err) {
      console.error('Failed to remove funnel tag', err);
    } finally {
      setUpdatingId(null);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Handle drag end with improved reordering logic
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDraggingId(null);
    if (over && active.id !== over.id) {
      setIsReordering(true);
      const oldIndex = displayedFunnelTags.findIndex(
        (t) => t._id === active.id
      );
      const newIndex = displayedFunnelTags.findIndex((t) => t._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedTags = arrayMove(
          displayedFunnelTags,
          oldIndex,
          newIndex
        );
        setDisplayedFunnelTags(reorderedTags);
        setHasUnsavedChanges(true);
      }
      setIsReordering(false);
    }
  };

  return (
    <div className='max-w-full'>
      <ScrollArea
        className={`w-full whitespace-nowrap rounded-xl ${
          hasUnsavedChanges
            ? 'border border-primary bg-primary/10 ring-1 ring-primary'
            : ''
        }`}
      >
        <div
          className='mx-auto flex w-max items-center gap-2 p-4'
          style={{ minHeight: 80 }}
        >
          {/* Start */}
          <div className={CARD_STYLE + ' text-base font-semibold'}>
            Assigned
          </div>
          <ChevronRight className='h-6 w-6 text-muted-foreground' />

          {/* DnD context for funnel tags */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={({ active }) => setDraggingId(active.id)}
          >
            <SortableContext
              items={funnelTagIds}
              strategy={horizontalListSortingStrategy}
            >
              {loading ? (
                <div className={CARD_STYLE + ' text-sm text-muted-foreground'}>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Loading tags...
                </div>
              ) : (
                displayedFunnelTags.map((tag, index) => (
                  <SortableTagCard
                    key={tag._id}
                    tag={tag}
                    id={tag._id}
                    onRemove={handleRemove}
                    draggingId={draggingId}
                    isUpdating={updatingId === tag._id || isReordering}
                    isNew={!initialFunnelTagIds.has(tag._id)}
                    showArrow={index < displayedFunnelTags.length - 1}
                  />
                ))
              )}
            </SortableContext>
          </DndContext>

          {/* This part handles the plus button and its preceding arrow */}
          {displayedFunnelTags.length < 5 && (
            <>
              {/* Arrow after the tags, before the plus button */}
              {(displayedFunnelTags.length > 0 || loading) && (
                <ChevronRight className='h-6 w-6 text-muted-foreground' />
              )}
              {/* Plus icon for adding */}
              <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    className='h-12 min-w-[48px] rounded-lg border bg-background'
                    aria-label='Add tag'
                  >
                    <Plus className='h-5 w-5' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-[200px] p-0' align='center'>
                  <ScrollArea className='h-[300px] w-full rounded-md'>
                    {availableTags.length === 0 ? (
                      <div className='p-4 text-center text-sm text-muted-foreground'>
                        No pipeline tags available
                      </div>
                    ) : (
                      <div className='p-4'>
                        {availableTags.map((tag) => (
                          <Button
                            key={tag._id}
                            variant='ghost'
                            className='w-full justify-start font-normal'
                            onClick={() => {
                              setDisplayedFunnelTags((prevTags) => [
                                ...prevTags,
                                tag,
                              ]);
                              setOpen(false);
                              setHasUnsavedChanges(true);
                            }}
                          >
                            <div
                              className='mr-2 h-3 w-3 rounded-full'
                              style={{
                                backgroundColor: tag.colour || '#94A3B8',
                              }}
                            />
                            {tag.name}
                          </Button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </>
          )}

          {/* Arrow before Sales - shown if we have any tags or if plus button is visible */}
          {(displayedFunnelTags.length > 0 ||
            displayedFunnelTags.length < 5) && (
            <ChevronRight className='h-6 w-6 text-muted-foreground' />
          )}

          {/* End */}
          <div className={CARD_STYLE + ' text-base font-semibold'}>Sales</div>
        </div>
        <ScrollBar orientation='horizontal' />
      </ScrollArea>

      {/* Save button below the tag list */}
      {hasUnsavedChanges && (
        <div className='mt-6 flex justify-end'>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className='gap-2 rounded-lg bg-primary px-8 py-2 shadow-md transition-colors hover:bg-primary/90'
            style={{ minWidth: 150 }}
          >
            {isSaving ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FunnelTagEditor;
